import { useMemo, useRef, useState } from "react";
import { useReviewUndoHistory } from "#use-review-undo-history";
import type { LearnActiveDeckCard } from "#learn-active-deck-card";

export type AnswerInputState = {
  /** Set when the review flow commits an answer on reveal (distinct from `input`). */
  answer: string;
  /** Live text in the answer field (typing, paste, voice). */
  input: string;
  /**
   * Rating preselected on the card-back. Mirrors the FSRS rating ordinal:
   * 1 = Again, 2 = Hard, 3 = Good, 4 = Easy. `null` means no preselection.
   */
  presetRating: 1 | 2 | 3 | 4 | null;
  validationState: "correct" | "incorrect" | null;
};

export function areAnswerInputStatesEqual(a: AnswerInputState, b: AnswerInputState): boolean {
  return (
    a.answer === b.answer &&
    a.input === b.input &&
    a.presetRating === b.presetRating &&
    a.validationState === b.validationState
  );
}

/**
 * Left by Learn's Start (on the summary card, or the map home's Welcome Back) just before the deck
 * replaces the route it opened from. The deck session that entry opens consumes it, which lets Undo
 * on that session's first card return to where the learner started.
 */
export type LearnSummaryDeckEntry = {
  /** Start was pressed on the map home: Undo returns to it, rather than to the summary card. */
  returnToMapHome: boolean;
};

export function useLearnLayoutContextValue() {
  // Set by the deck host while it shows its preview of the next card behind the summary card.
  // Host state, not a readiness report: the summary only uses it to decide whether to draw its own
  // empty card underlay, and nothing waits on it.
  const [hasSummaryPreview, setHasSummaryPreview] = useState(false);
  const [summaryCardHeight, setSummaryCardHeight] = useState<number | null>(null);
  // Shared by deck and route actions. Only a committed resolution consumes it; speculative
  // successor/buffer preparation must not.
  const dailyReviewCompletedRef = useRef(false);
  const [reviewCelebration, setReviewCelebration] = useState(0);
  // Answer-input state lives here (above the learn navigator) so it survives navigation
  // between learn screens — the previous screen blurring/unmounting no longer resets it.
  const [answerInputState, setAnswerInputState] = useState<AnswerInputState>({
    answer: "",
    input: "",
    presetRating: null,
    validationState: null,
  });

  const answerInputKeyboardSubmitRef = useRef<(() => void) | null>(null);

  // A ref rather than state: it is written and consumed around one navigation, and nothing renders
  // from it. See `LearnSummaryDeckEntry`.
  const summaryDeckEntryRef = useRef<LearnSummaryDeckEntry | null>(null);

  // The card the focused deck is showing, for persistent Learn chrome (toolbar pills, header
  // expansion) that sits OUTSIDE the deck route and must not read the router's global params.
  const [activeDeckCard, setActiveDeckCard] = useState<LearnActiveDeckCard | null>(null);
  // Identity of the step the focused deck shows (its session, card occurrence and face), or `null`
  // while no deck is focused. Changes on every reveal, rating and Undo. Native decks keep their
  // history out of the route, so chrome that follows deck steps (study-activity bookkeeping) reads
  // this instead of route params.
  const [activeDeckStep, setActiveDeckStep] = useState<string | null>(null);

  // Lives at this level (not on the drill page) because review navigation replaces the card
  // screen rather than pushing it — anything card-scoped is gone by the time undo is offered.
  const {
    canRedoReview,
    canUndoReview,
    peekReviewRedoEntry,
    peekReviewUndoEntry,
    popReviewRedoEntry,
    popReviewUndoEntry,
    pushReviewUndoEntry,
  } = useReviewUndoHistory();

  // Memoized because every keystroke in the answer field updates `answerInputState`. A fresh
  // object literal here changed the context identity on each character, re-running every
  // `useContextSelectors` consumer (and `DrillPageContext`, which republishes this value) —
  // work that lengthened the JS round trip the native input's staleness guard has to absorb.
  return useMemo(
    () => ({
      activeDeckCard,
      activeDeckStep,
      answerInputKeyboardSubmitRef,
      answerInputState,
      canRedoReview,
      canUndoReview,
      dailyReviewCompletedRef,
      hasSummaryPreview,
      peekReviewRedoEntry,
      peekReviewUndoEntry,
      popReviewRedoEntry,
      popReviewUndoEntry,
      pushReviewUndoEntry,
      reviewCelebration,
      setActiveDeckCard,
      setActiveDeckStep,
      setAnswerInputState,
      setHasSummaryPreview,
      setReviewCelebration,
      setSummaryCardHeight,
      summaryCardHeight,
      summaryDeckEntryRef,
    }),
    [
      hasSummaryPreview,
      summaryCardHeight,
      activeDeckCard,
      activeDeckStep,
      answerInputKeyboardSubmitRef,
      answerInputState,
      canRedoReview,
      canUndoReview,
      reviewCelebration,
      peekReviewRedoEntry,
      peekReviewUndoEntry,
      popReviewRedoEntry,
      popReviewUndoEntry,
      pushReviewUndoEntry,
      setAnswerInputState,
    ],
  );
}

export type LearnLayoutContextValue = ReturnType<typeof useLearnLayoutContextValue>;
